import React, { createContext, useContext, useState, useCallback, useEffect, ReactNode } from 'react';

// Link color types
export type LinkColor = 'none' | 'red' | 'green' | 'blue' | 'yellow';

// Entity types that can be selected and shared between panels
export interface SelectedEntity {
  type: 'country' | 'sector' | 'metric' | 'event';
  id: string;
  label: string;
  data?: Record<string, unknown>;
}

// Colors in order for cycling
export const LINK_COLORS: LinkColor[] = ['none', 'red', 'green', 'blue', 'yellow'];

// Color hex values for styling
export const LINK_COLOR_VALUES: Record<LinkColor, string> = {
  none: '#4b5563', // gray-600
  red: '#ff3b3b',
  green: '#00d26a',
  blue: '#00a3ff',
  yellow: '#ffb800',
};

// Context state interface
interface PanelLinkState {
  panelColors: Record<string, LinkColor>;
  colorSelections: Record<LinkColor, SelectedEntity | null>;
}

// Context value interface
interface PanelLinkContextType {
  // Get current color for a panel
  getPanelColor: (panelId: string) => LinkColor;

  // Set color for a panel
  setPanelColor: (panelId: string, color: LinkColor) => void;

  // Cycle to next color for a panel
  cyclePanelColor: (panelId: string) => LinkColor;

  // Get currently selected entity for a color
  getColorSelection: (color: LinkColor) => SelectedEntity | null;

  // Broadcast selection from a panel (updates all same-color panels)
  broadcastSelection: (panelId: string, entity: SelectedEntity) => void;

  // Clear selection for a color
  clearColorSelection: (color: LinkColor) => void;

  // Get all panels with a specific color
  getPanelsByColor: (color: LinkColor) => string[];

  // Subscribe to selection changes for a color
  subscribeToColor: (color: LinkColor, callback: (entity: SelectedEntity | null) => void) => () => void;
}

const STORAGE_KEY = 'sr2-panel-links';

// Default state
const defaultState: PanelLinkState = {
  panelColors: {},
  colorSelections: {
    none: null,
    red: null,
    green: null,
    blue: null,
    yellow: null,
  },
};

// Load state from localStorage
const loadState = (): PanelLinkState => {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved) {
      const parsed = JSON.parse(saved);
      return {
        panelColors: parsed.panelColors || {},
        colorSelections: {
          ...defaultState.colorSelections,
          ...(parsed.colorSelections || {}),
        },
      };
    }
  } catch (e) {
    console.warn('Failed to load panel link state:', e);
  }
  return defaultState;
};

// Save state to localStorage
const saveState = (state: PanelLinkState) => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch (e) {
    console.warn('Failed to save panel link state:', e);
  }
};

// Create context
const PanelLinkContext = createContext<PanelLinkContextType | null>(null);

// Provider component
export const PanelLinkProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [state, setState] = useState<PanelLinkState>(loadState);
  const [subscribers, setSubscribers] = useState<Map<LinkColor, Set<(entity: SelectedEntity | null) => void>>>(
    new Map()
  );

  // Persist state changes
  useEffect(() => {
    saveState(state);
  }, [state]);

  // Get panel color
  const getPanelColor = useCallback((panelId: string): LinkColor => {
    return state.panelColors[panelId] || 'none';
  }, [state.panelColors]);

  // Set panel color
  const setPanelColor = useCallback((panelId: string, color: LinkColor) => {
    setState(prev => ({
      ...prev,
      panelColors: {
        ...prev.panelColors,
        [panelId]: color,
      },
    }));
  }, []);

  // Cycle to next color
  const cyclePanelColor = useCallback((panelId: string): LinkColor => {
    const currentColor = state.panelColors[panelId] || 'none';
    const currentIndex = LINK_COLORS.indexOf(currentColor);
    const nextIndex = (currentIndex + 1) % LINK_COLORS.length;
    const nextColor = LINK_COLORS[nextIndex];

    setState(prev => ({
      ...prev,
      panelColors: {
        ...prev.panelColors,
        [panelId]: nextColor,
      },
    }));

    return nextColor;
  }, [state.panelColors]);

  // Get selection for a color
  const getColorSelection = useCallback((color: LinkColor): SelectedEntity | null => {
    return state.colorSelections[color];
  }, [state.colorSelections]);

  // Broadcast selection
  const broadcastSelection = useCallback((panelId: string, entity: SelectedEntity) => {
    const color = state.panelColors[panelId] || 'none';

    // Only broadcast if panel is linked (not 'none')
    if (color === 'none') return;

    setState(prev => ({
      ...prev,
      colorSelections: {
        ...prev.colorSelections,
        [color]: entity,
      },
    }));

    // Notify subscribers
    const colorSubscribers = subscribers.get(color);
    if (colorSubscribers) {
      colorSubscribers.forEach(callback => callback(entity));
    }
  }, [state.panelColors, subscribers]);

  // Clear selection for a color
  const clearColorSelection = useCallback((color: LinkColor) => {
    setState(prev => ({
      ...prev,
      colorSelections: {
        ...prev.colorSelections,
        [color]: null,
      },
    }));

    // Notify subscribers
    const colorSubscribers = subscribers.get(color);
    if (colorSubscribers) {
      colorSubscribers.forEach(callback => callback(null));
    }
  }, [subscribers]);

  // Get panels by color
  const getPanelsByColor = useCallback((color: LinkColor): string[] => {
    return Object.entries(state.panelColors)
      .filter(([, c]) => c === color)
      .map(([panelId]) => panelId);
  }, [state.panelColors]);

  // Subscribe to color changes
  const subscribeToColor = useCallback((color: LinkColor, callback: (entity: SelectedEntity | null) => void) => {
    setSubscribers(prev => {
      const newMap = new Map(prev);
      const colorSet = newMap.get(color) || new Set();
      colorSet.add(callback);
      newMap.set(color, colorSet);
      return newMap;
    });

    // Return unsubscribe function
    return () => {
      setSubscribers(prev => {
        const newMap = new Map(prev);
        const colorSet = newMap.get(color);
        if (colorSet) {
          colorSet.delete(callback);
        }
        return newMap;
      });
    };
  }, []);

  const value: PanelLinkContextType = {
    getPanelColor,
    setPanelColor,
    cyclePanelColor,
    getColorSelection,
    broadcastSelection,
    clearColorSelection,
    getPanelsByColor,
    subscribeToColor,
  };

  return (
    <PanelLinkContext.Provider value={value}>
      {children}
    </PanelLinkContext.Provider>
  );
};

// Hook to use the context
export const usePanelLinkContext = (): PanelLinkContextType => {
  const context = useContext(PanelLinkContext);
  if (!context) {
    throw new Error('usePanelLinkContext must be used within a PanelLinkProvider');
  }
  return context;
};

export default PanelLinkContext;
